// Dependencies
import { Scrollbars } from 'react-custom-scrollbars-2';
import { connect } from 'react-redux';

import Button from 'components/atoms/Button';
import FormGroup from 'components/atoms/FormGroup';
import Icon from 'components/atoms/Icon';
import Image from 'components/atoms/Image';
import Input from 'components/atoms/Input';
import TypeTag from 'components/atoms/TypeTag';
import ManagerBase from 'components/baseClass/ManagerBase';
import { PADGE_LIMIT } from 'data/static';
import { bindActionCreators, Dispatch } from 'redux';
import { userRecords } from 'services/users';
import {
    SetErrorHandlerAction,
    setErrorHandlerAction,
    setSystemLoadingAction,
    SetSystemLoadingAction,
} from 'store/actions/errorsActions';
import { OpenModal, PortType, RootState, UserPortType, UserRuleType, UserType } from 'types';
import { DateTimeFormat } from 'utils/dateTimeFormat';
import { clone } from 'utils/formHandlers';
import { translate } from 'utils/translate';
import { twclsx } from 'utils/twclsx';

interface UsersComponentProps {
    active: boolean;
    opendMenu?: boolean;
    setSystemLoadingAction: SetSystemLoadingAction;
    port: PortType;
    setErrorHandlerAction: SetErrorHandlerAction;
    openModelFunction: OpenModal;
    dateTimeFormat: DateTimeFormat;
    user: UserType;
}

interface UsersComponentState {
    active: boolean;

    opendMenu?: boolean;

    page: number;
    limit: number;
    searchFor: string[];
    order: {
        type: string;
        order: string;
    };
    searchQuery: any;
    searchValue: string;
    filterQuery: any;
    loading: boolean;
    pages: number;
    total: number;
    items: UserType[];
}

class Users extends ManagerBase<UsersComponentProps, UsersComponentState> {
    constructor(props: UsersComponentProps) {
        super(props);
        this.state = {
            active: false,
            searchFor: ['last_name', 'email', 'first_name'],
            opendMenu: false,
            searchValue: '',
            pages: 0,
            page: 1,
            limit: PADGE_LIMIT,
            total: 0,
            order: { type: 'last_active', order: 'desc' },
            searchQuery: {},
            filterQuery: {},
            loading: false,
            items: [],
        };
    }

    static getDerivedStateFromProps(props: UsersComponentProps, state: UsersComponentState) {
        if (props.active !== state.active || props.opendMenu !== state.opendMenu) {
            return {
                active: props.active,
                opendMenu: props.opendMenu,
            };
        }

        return null;
    }

    componentDidMount(): void {
        this.getData();
    }

    getData = () => {
        this.setState({ loading: true }, () => {
            userRecords(this.props.port.id, {
                page: this.state.page, // send page number
                limit: this.state.limit,
                order: this.state.order, // send order value
                search: this.state.searchQuery, // send search value
                filter: this.state.filterQuery,
            })
                .then((res) => {
                    //update state and save the data
                    this.setState({
                        pages: Math.ceil(res.total / this.state.limit),
                        total: res.total,
                        items: res.items,
                        loading: false,
                    });
                    this.props.setSystemLoadingAction(false);
                })
                .catch((error) => {
                    this.props.setErrorHandlerAction(error);
                });
        });
    };

    togglDeleteUser = (id) => {
        this.props.openModelFunction(
            'deleteUserModel',
            0,
            'medium',
            { id: id },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('deleteUserModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    togglDeleteToken = (id) => {
        this.props.openModelFunction(
            'deleteTokenModel',
            0,
            'medium',
            { id: id },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('deleteTokenModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    togglGetToken = (id) => {
        this.props.openModelFunction(
            'getTokenModel',
            0,
            'medium',
            { id: id },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('getTokenModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    toggleCreateuser = () => {
        this.props.openModelFunction(
            'createUserModel',
            0,
            'medium',
            {},
            {
                onCancel: async () => {
                    await this.props.openModelFunction('createUserModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    togglAddUserPortModal = (user: UserType) => {
        this.props.openModelFunction(
            'createUserPortModel',
            0,
            'medium',
            { user },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('createUserPortModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    togglEditUserPortModal = (user: UserType, port: UserPortType, rules: UserRuleType[]) => {
        this.props.openModelFunction(
            'editUserPortModel',
            0,
            'medium',
            { user, port, rules },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('editUserPortModel');
                },
                togglRestrictUserModal: async () => {
                    await this.props.openModelFunction('editUserPortModel');
                    this.togglRestrictUserModal(port.port_id, port.id);
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    togglRestrictUserModal = (port_id, id) => {
        this.props.openModelFunction(
            'restrictUserModel',
            0,
            'medium',
            { port_id: port_id, id: id },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('restrictUserModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    toggleEdituser = (user: UserType) => {
        this.props.openModelFunction(
            'editUserModel',
            0,
            'medium',
            {
                user: user,
            },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('editUserModel');
                },
                onConfirm: this.getData,
            },
            true
        );
    };

    toggleFilter = () => {
        this.props.openModelFunction(
            'userFilterModel',
            0,
            'medium',
            {
                filter: this.state.filterQuery,
            },
            {
                onCancel: async () => {
                    await this.props.openModelFunction('userFilterModel');
                },
                onConfirm: this.setFilter,
            },
            true
        );
    };

    timeAgo = (date) => {
        return this.props.dateTimeFormat.now().timeAgo(this.props.dateTimeFormat.parseDateTime(date));
    };

    dateFormater = (date) => {
        return this.props.dateTimeFormat
            .parseDateTime(date)
            .date.setZone('local', { keepLocalTime: false })
            .toFormat('dd MMM yyyy HH:mm (Z)');
    };

    render() {
        return (
            <div
                className={twclsx(
                    !this.state.active && 'hidden',
                    'mt-14',
                    'h-page',
                    'border-gray-300',
                    'padding-transition',
                    'fixed',
                    'z-10',
                    'left-0',
                    'right-0',
                    'margin-transition',
                    this.state.opendMenu ? 'ml-menu w-main' : 'ml-14 w-fullPage'
                )}
            >
                <div className="flex no-select h-12 justify-between border-b border-gray-300  px-2">
                    <div className=" py-2  flex items-center">
                        <div className="py-0 px-0 w-1/4">
                            <FormGroup required={false} className="mb-0 mt-0 relative">
                                <Input
                                    value={this.state.searchValue}
                                    name="search"
                                    type="search"
                                    onChange={this.search}
                                    className="py-1 pl-5 w-48"
                                ></Input>
                                <Icon className="absolute left-1 bottom-1 mb-1  w-4 h-4">
                                    <path d="M10 10m-7 0a7 7 0 1 0 14 0a7 7 0 1 0 -14 0" />
                                    <path d="M21 21l-6 -6" />
                                </Icon>
                            </FormGroup>
                        </div>
                    </div>
                    <div className=" py-2 flex justify-end">
                        <Button
                            onClick={this.toggleCreateuser}
                            type="button"
                            className="px-4 py-1 bg-primary-submit rounded-md text-white"
                        >
                            {translate('Create new user')}
                        </Button>
                    </div>
                </div>
                <div className="grid  no-select grid-cols-24 border-b border-gray-300  ">
                    <div className=" py-2 col-span-1 flex items-center">
                        {this.renderOrder('id')}
                        {translate('#')}
                    </div>
                    <div className=" py-2 col-span-4 flex items-center">
                        {this.renderOrder('email')}
                        {translate('User')}
                    </div>
                    <div className=" py-2 col-span-3 flex items-center">
                        {this.renderOrder('last_active')} {translate('Date last seen')}
                    </div>
                    <div className=" py-2 col-span-6 flex items-center">
                        {this.renderFilter('port_id')}
                        {translate('User ports')}
                    </div>
                    <div className=" py-2 col-span-2 flex items-center">
                        {this.renderOrder('type')}
                        {translate('Cluster Administrator')}
                    </div>
                    {this.props.user.type === 'systemAdministrator' && (
                        <div className=" py-2 col-span-2 flex items-center">
                            {this.renderOrder('long_token')}
                            {translate('Long life token')}
                        </div>
                    )}
                    <div className=" py-2 col-span-3 flex items-center">
                        {this.renderOrder('date_joined')}
                        {translate('Date join')}
                    </div>
                    {this.props.user.type === 'systemAdministrator' && (
                        <div className=" py-2 col-span-3 flex items-center">{translate('OPTIONS')}</div>
                    )}
                </div>
                {this.state.loading ? (
                    <div
                        style={{
                            height: 'calc( 100vh - 190px)',
                        }}
                        className="flex items-center justify-center "
                    >
                        <div className="flex justify-start items-center gap-1 max-w-3xl mx-auto text-center  leading-9   text-gray-900">
                            <Image src="/static/images/loading.svg" className="mx-auto w-4" alt="error" />
                            <p>{translate('Loading, Please Wait...')}</p>
                        </div>
                    </div>
                ) : (
                    <>
                        <Scrollbars
                            renderThumbHorizontal={() => <div style={{ display: 'none' }} />}
                            renderTrackHorizontal={() => <div style={{ display: 'none' }} />}
                            style={{
                                height: 'calc( 100vh - 190px)',
                            }}
                        >
                            {this.state.items.map((item, index) => {
                                return (
                                    <div
                                        key={index}
                                        className={twclsx(
                                            'grid',
                                            'raw',
                                            'grid-cols-24',
                                            'border-b',
                                            'border-gray-300',

                                            'relative'
                                        )}
                                    >
                                        <div className=" py-2 px-2 col-span-1 flex items-center">{item.id}</div>
                                        <div className=" py-2 col-span-4 flex flex-col text-left  ">
                                            <div>
                                                {item.first_name} {item.last_name}
                                            </div>
                                            <div>
                                                {item.active == 'inactive' ? (
                                                    <span className="px-2 mr-4 inline-flex text-xs capitalize leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                                        {item.active}
                                                    </span>
                                                ) : null}

                                                {item.active == 'active' ? (
                                                    <span className="px-2 mr-4 inline-flex text-xs capitalize leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                                        {item.active}
                                                    </span>
                                                ) : null}

                                                {item.email}
                                            </div>
                                        </div>

                                        <div className="  col-span-3 flex items-center">
                                            {this.timeAgo(item.last_active)} <br></br>
                                            {this.dateFormater(item.last_active)}
                                        </div>
                                        <div className=" py-2 col-span-6  items-center relative">
                                            {this.props.user.type === 'systemAdministrator' && (
                                                <span
                                                    onClick={() => {
                                                        this.togglAddUserPortModal(item);
                                                    }}
                                                    className="positioning px-2 py-1 text-sm rounded-full items-center cursor-pointer inline-flex font-medium leading-4 hide-border whitespace-nowrap capitalize mr-2"
                                                >
                                                    <Icon className="w-3 h-3 mr-1">
                                                        <path d="M12 5l0 14"></path>
                                                        <path d="M5 12l14 0"></path>
                                                    </Icon>
                                                    {translate('Add port')}
                                                </span>
                                            )}

                                            {item.user_ports
                                                .filter((x) => {
                                                    return (
                                                        this.props.user.user_ports
                                                            .map((y) => {
                                                                return y.port_id;
                                                            })
                                                            .indexOf(x.port_id) >= 0 ||
                                                        (this.props.user &&
                                                            this.props.user.type == 'systemAdministrator')
                                                    );
                                                })
                                                .sort(function (a, b) {
                                                    return a.port_id - b.port_id;
                                                })
                                                .map((port) => {
                                                    return (
                                                        <div
                                                            className="   items-center inline-flex "
                                                            key={port.id}
                                                            onClick={() => {
                                                                this.togglEditUserPortModal(
                                                                    item,
                                                                    port,
                                                                    item.user_rules
                                                                );
                                                            }}
                                                        >
                                                            <TypeTag
                                                                className="whitespace-nowrap capitalize  mr-2 mb-1"
                                                                type="positioning"
                                                            >
                                                                <>
                                                                    <Icon className="w-3 h-3 mr-1">
                                                                        <path d="M4 20h4l10.5 -10.5a2.828 2.828 0 1 0 -4 -4l-10.5 10.5v4"></path>
                                                                        <path d="M13.5 6.5l4 4"></path>
                                                                        <path d="M16 19h6"></path>
                                                                    </Icon>
                                                                    {port.port.name}
                                                                </>
                                                            </TypeTag>
                                                        </div>
                                                    );
                                                })}
                                        </div>
                                        <div className="py-2 col-span-2 flex items-center">
                                            {item.type === 'systemAdministrator' ? translate('Yes') : translate('No')}
                                        </div>
                                        {this.props.user.type === 'systemAdministrator' && (
                                            <div
                                                onClick={() => item.long_token == 'true' && this.togglGetToken(item.id)}
                                                className={twclsx(
                                                    'py-2',
                                                    'col-span-2',
                                                    'flex',
                                                    'items-center',
                                                    item.long_token == 'true' && 'text-blue-500 cursor-pointer'
                                                )}
                                            >
                                                {item.long_token == 'true' ? translate('Yes') : translate('No')}
                                            </div>
                                        )}

                                        <div className=" py-2 col-span-3 flex items-center">
                                            {this.dateFormater(item.created_at)}
                                        </div>

                                        <div className=" py-2 gap-1  col-span-3 flex items-center">
                                            <Button
                                                type="button"
                                                onClick={() => {
                                                    this.toggleEdituser(clone(item));
                                                }}
                                                className="px-2 py-0 bg-primary-submit text-sm rounded-md text-white "
                                            >
                                                {translate('Edit')}
                                            </Button>
                                            {(item.type !== 'systemAdministrator' ||
                                                this.props.user.type === 'systemAdministrator') && (
                                                <Button
                                                    type="button"
                                                    onClick={() => {
                                                        this.togglDeleteUser(item.id);
                                                    }}
                                                    className="px-2 py-0 bg-primary-delete text-sm rounded-md text-white"
                                                >
                                                    {translate('Delete')}
                                                </Button>
                                            )}
                                            {this.props.user.type === 'systemAdministrator' && (
                                                <Button
                                                    type="button"
                                                    onClick={() => {
                                                        this.togglDeleteToken(item.id);
                                                    }}
                                                    className="px-2 py-0 bg-primary-delete text-sm rounded-md text-white"
                                                >
                                                    {translate('Close session')}
                                                </Button>
                                            )}
                                        </div>
                                    </div>
                                );
                            })}
                        </Scrollbars>
                    </>
                )}
                <div className="flex h-10 p-1 justify-between border-t border-gray-300  ">
                    <div className="w-36 flex text-center ">{this.renderPages()}</div>
                    <p className="  py-2 px-2 text-gray-700">
                        {translate('pagination', {
                            showingFrom: (this.state.page - 1) * this.state.limit + 1,
                            showingTo:
                                (this.state.page - 1) * this.state.limit + this.state.limit > this.state.total
                                    ? this.state.total
                                    : (this.state.page - 1) * this.state.limit + this.state.limit,
                            of: this.state.total,
                        })}
                    </p>
                </div>
            </div>
        );
    }
}

const usersStateToProps = (state: RootState) => ({
    port: state.port.port,
    openModelFunction: state.layout.openModelFunction,
    dateTimeFormat: state.port.dateTimeFormat,
    user: state.user.user,
});

const usersDispatchToProps = (dispatch: Dispatch) => ({
    setSystemLoadingAction: bindActionCreators(setSystemLoadingAction, dispatch),
    setErrorHandlerAction: bindActionCreators(setErrorHandlerAction, dispatch),
});

export default connect(usersStateToProps, usersDispatchToProps)(Users);
